<script setup lang="ts" generic="Row extends Record<string, unknown>">
import type { CSSProperties } from 'vue'

import type { SortState, TableColumn, TableDensity } from './types'

import { Icon } from '@/shared/ui/icon'
import Tooltip from '@/shared/ui/tooltip/Tooltip.vue'

defineProps<{
  column: TableColumn<Row>
  sort: SortState
  cellStyle: CSSProperties
  draggable: boolean
  frozen: boolean
  freezeEnabled: boolean
  freezeLabel: string | undefined
  resizeLabel: string | undefined
  density: TableDensity
  columnExpandable: boolean
  columnExpanded: boolean
  columnExpandLabel: string | undefined
}>()

defineEmits<{
  sort: []
  freeze: []
  'expand-column': []
  dragstart: [event: DragEvent]
  dragover: [event: DragEvent]
  drop: [event: DragEvent]
  dragend: []
  resize: [event: PointerEvent]
}>()
</script>

<template>
  <th
    class="ui-table-th group box-border border-b border-e text-sm border-[color:var(--ui-table-grid)] p-0 text-start text-[var(--ui-table-header-size)] font-[var(--ui-table-header-weight)] text-[color:var(--ui-table-header-fg)] last:border-e-0"
    :class="[
      `ui-table-th--${column.align ?? (column.numeric ? 'end' : 'start')}`,
      `ui-table-th--${density}`,
      {
        'cursor-grab active:cursor-grabbing': draggable,
        'text-center': (column.align ?? (column.numeric ? 'end' : 'start')) === 'center',
        'text-end': (column.align ?? (column.numeric ? 'end' : 'start')) === 'end',
        relative: !frozen,
        'z-raised': !frozen,
        'ui-table-th--frozen sticky z-elevated': frozen,
        'ui-table-th--supplementary bg-[color:var(--ui-table-supplementary-header-bg)]':
          column.supplementary,
        'bg-[color:var(--ui-table-header-bg)]': !column.supplementary,
      },
    ]"
    :style="cellStyle"
    scope="col"
    :draggable="draggable"
    @click="column.sortable && $emit('sort')"
    @dragstart="$emit('dragstart', $event)"
    @dragover.prevent="$emit('dragover', $event)"
    @drop.prevent="$emit('drop', $event)"
    @dragend="$emit('dragend')"
  >
    <div
      class="ui-table-th__content flex w-full items-center px-xs py-xs leading-[var(--ui-table-cell-line-height)]"
      :class="[
        (column.align ?? (column.numeric ? 'end' : 'start')) === 'center'
          ? 'justify-center'
          : (column.align ?? (column.numeric ? 'end' : 'start')) === 'end'
            ? 'justify-end text-right'
            : 'justify-start',
        { 'py-xs': density === 'compact' },
      ]"
    >
      <button
        v-if="column.sortable"
        class="ui-table-th__sort flex min-w-0 flex-1 cursor-pointer items-center gap-xs whitespace-normal border-0 bg-transparent p-0 text-inherit [font:inherit] [text-align:inherit] focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ui-color-ring)]"
        :class="column.numeric ? 'flex-row-reverse' : undefined"
        type="button"
        :aria-pressed="sort?.field === column.field"
      >
        <Tooltip v-if="column.description" :text="column.description">
          <slot>{{ column.header }}</slot>
        </Tooltip>
        <slot v-else>{{ column.header }}</slot>
        <span
          v-if="sort?.field === column.field"
          class="ui-table-th__sort-slot flex w-4 min-w-4 justify-center"
          aria-hidden="true"
        >
          <Icon
            class="ui-table-th__sort-icon flex-none text-info"
            :name="sort?.dir === 'asc' ? 'arrow-up-long' : 'arrow-down-long'"
            :size="14"
          />
        </span>
      </button>
      <span v-else class="ui-table-th__label min-w-0 flex-1">
        <Tooltip v-if="column.description" :text="column.description">
          <slot>{{ column.header }}</slot>
        </Tooltip>
        <slot v-else>{{ column.header }}</slot>
      </span>

      <button
        v-if="freezeEnabled"
        class="ui-table-th__freeze flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-muted opacity-0 transition-opacity hover:text-info hover:opacity-100 focus-visible:text-info focus-visible:opacity-100 focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ui-color-ring)] group-hover:opacity-50"
        :class="{ 'opacity-100': frozen, 'order-first': column.numeric }"
        type="button"
        :aria-label="freezeLabel ?? column.header"
        :aria-pressed="frozen"
        @click.stop="$emit('freeze')"
      >
        <Icon :name="frozen ? 'lock' : 'lock-open'" :size="10" aria-hidden="true" />
      </button>

      <button
        v-if="columnExpandable"
        class="ui-table-th__expand-column ml-xs flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-muted opacity-50 transition-opacity hover:text-info hover:opacity-100 focus-visible:text-info focus-visible:opacity-100 focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ui-color-ring)]"
        :class="{ 'opacity-100 flex-row-reverse': columnExpanded }"
        type="button"
        :aria-label="columnExpandLabel ?? column.header"
        :aria-pressed="columnExpanded"
        @click.stop="$emit('expand-column')"
      >
        <Icon name="chevron-left" :size="10" aria-hidden="true" />
        <Icon name="chevron-right" :size="10" aria-hidden="true" />
      </button>
    </div>

    <button
      v-if="column.resizable"
      class="ui-table-th__resize absolute inset-y-0 -end-2xs z-floating w-xs cursor-ew-resize border-0 bg-transparent p-0 focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-[calc(-1*var(--ui-ring-width))] focus-visible:outline-[color:var(--ui-color-ring)]"
      type="button"
      :aria-label="resizeLabel ?? column.header"
      @pointerdown.prevent.stop="$emit('resize', $event)"
      @click.stop
    >
      <span
        class="absolute inset-y-1/4 start-[calc(50%-0.5px)] w-px bg-border-strong"
        aria-hidden="true"
      />
    </button>
  </th>
</template>

<style scoped>
.ui-table-th :deep(.ui-metric-view__text) {
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
